const STORAGE_KEY = "agent-system-settings";

export const SETTING_KEYS = {
  addresses: "addressOptions",
  nationalities: "nationalityOptions",
  jobTypes: "jobTypeOptions",
  paymentOptions: "paymentOptions",
  shifts: "shiftOptions",
};

export const SETTING_SECTIONS = [
  {
    key: SETTING_KEYS.addresses,
    permissionKey: "settingsAddresses",
    permissionLabel: "Address Options",
    path: "/settings/addresses",
    menuKey: "addresses",
    iconKey: "addresses",
    title: "Addresses",
    description: "Manage address values used by employees and employers.",
    itemLabel: "Address",
  },
  {
    key: SETTING_KEYS.nationalities,
    permissionKey: "settingsNationalities",
    permissionLabel: "Nationality Options",
    path: "/settings/nationalities",
    menuKey: "nationalities",
    iconKey: "nationalities",
    title: "Nationalities",
    description: "Manage nationality values used in the employee form.",
    itemLabel: "Nationality",
  },
  {
    key: SETTING_KEYS.jobTypes,
    permissionKey: "settingsJobTypes",
    permissionLabel: "Job Type Options",
    path: "/settings/job-types",
    menuKey: "jobTypes",
    iconKey: "jobTypes",
    title: "Job Types",
    description: "Manage job type values used in the order creation form.",
    itemLabel: "Job Type",
  },
  {
    key: SETTING_KEYS.paymentOptions,
    permissionKey: "settingsPaymentOptions",
    permissionLabel: "Payment Options",
    path: "/settings/payment-options",
    menuKey: "paymentOptions",
    iconKey: "paymentOptions",
    title: "Payment Options",
    description: "Manage payment methods available for payment records.",
    itemLabel: "Payment Option",
  },
  {
    key: SETTING_KEYS.shifts,
    permissionKey: "settingsShifts",
    permissionLabel: "Shift Options",
    path: "/settings/shifts",
    menuKey: "shifts",
    iconKey: "shifts",
    title: "Shifts",
    description: "Manage shift values available for orders.",
    itemLabel: "Shift",
  },
];

export const defaultAppSettings = {
  addressOptions: ["remote", "on-site", "hybrid", "riyadh", "jeddah"],
  nationalityOptions: ["saudi", "egyptian", "pakistani", "filipino"],
  jobTypeOptions: ["house_help", "driver", "chef", "nanny"],
  paymentOptions: ["cash", "visa", "mada", "bank_transfer"],
  shiftOptions: ["day", "night", "flexible"],
};

function normalizeOptions(options, fallbackOptions) {
  const asFallback = fallbackOptions.map((item) => {
    if (typeof item === "string") {
      return { value: item, label: item };
    }
    return {
      value: String(item?.value || "").trim(),
      label: String(item?.label || String(item?.value || "")).trim(),
    };
  });

  if (!Array.isArray(options)) {
    return asFallback;
  }

  const seen = new Set();
  const normalized = [];

  for (const item of options) {
    const value = typeof item === "string" ? item.trim() : String(item?.value || "").trim();
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    const label =
      typeof item === "string"
        ? value
        : String(item?.label || item?.value || value).trim();

    normalized.push({ value, label });
  }

  return normalized.length > 0 ? normalized : asFallback;
}

export function loadAppSettings() {
  if (typeof window === "undefined") {
    return defaultAppSettings;
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : null;
    return {
      addressOptions: normalizeOptions(stored?.addressOptions, defaultAppSettings.addressOptions),
      nationalityOptions: normalizeOptions(stored?.nationalityOptions, defaultAppSettings.nationalityOptions),
      jobTypeOptions: normalizeOptions(stored?.jobTypeOptions, defaultAppSettings.jobTypeOptions),
      paymentOptions: normalizeOptions(stored?.paymentOptions, defaultAppSettings.paymentOptions),
      shiftOptions: normalizeOptions(stored?.shiftOptions, defaultAppSettings.shiftOptions),
    };
  } catch (error) {
    console.error("Failed to load app settings", error);
    localStorage.removeItem(STORAGE_KEY);
    return defaultAppSettings;
  }
}

export function settingsLookLikeDefaults(settings) {
  const current = settings || loadAppSettings();
  return Object.values(SETTING_KEYS).every((key) => {
    const currentValues = (current[key] || [])
      .map((item) => (typeof item === "string" ? item : item.value))
      .join("|");
    const defaultValues = defaultAppSettings[key].join("|");
    return currentValues === defaultValues;
  });
}

function toApiPayload(settings) {
  return {
    addressOptions: (settings.addressOptions || []).map((item) => item.value || item),
    nationalityOptions: (settings.nationalityOptions || []).map((item) => item.value || item),
    jobTypeOptions: (settings.jobTypeOptions || []).map((item) => item.value || item),
    paymentOptions: (settings.paymentOptions || []).map((item) => item.value || item),
    shiftOptions: (settings.shiftOptions || []).map((item) => item.value || item),
  };
}

function fromApiPayload(payload) {
  return {
    addressOptions: normalizeOptions(payload?.addressOptions, defaultAppSettings.addressOptions),
    nationalityOptions: normalizeOptions(payload?.nationalityOptions, defaultAppSettings.nationalityOptions),
    jobTypeOptions: normalizeOptions(payload?.jobTypeOptions, defaultAppSettings.jobTypeOptions),
    paymentOptions: normalizeOptions(payload?.paymentOptions, defaultAppSettings.paymentOptions),
    shiftOptions: normalizeOptions(payload?.shiftOptions, defaultAppSettings.shiftOptions),
  };
}

export async function fetchRemoteAppSettings(api) {
  const response = await api.get("/settings");
  const settings = fromApiPayload(response.data?.settings);
  saveAppSettings(settings);
  return {
    settings,
    fromDefaults: Boolean(response.data?.from_defaults),
  };
}

export async function persistRemoteAppSettings(api, settings) {
  try {
    const response = await api.put("/settings", toApiPayload(settings));
    const saved = fromApiPayload(response.data?.settings || toApiPayload(settings));
    saveAppSettings(saved);
    return saved;
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Unable to save shared dropdown settings.";
    throw new Error(message);
  }
}

export function saveAppSettings(settings) {
  try {
    const normalized = {
      addressOptions: normalizeOptions(settings.addressOptions, defaultAppSettings.addressOptions),
      nationalityOptions: normalizeOptions(settings.nationalityOptions, defaultAppSettings.nationalityOptions),
      jobTypeOptions: normalizeOptions(settings.jobTypeOptions, defaultAppSettings.jobTypeOptions),
      paymentOptions: normalizeOptions(settings.paymentOptions, defaultAppSettings.paymentOptions),
      shiftOptions: normalizeOptions(settings.shiftOptions, defaultAppSettings.shiftOptions),
    };

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        addressOptions: normalized.addressOptions.map((item) => item.value),
        nationalityOptions: normalized.nationalityOptions.map((item) => item.value),
        jobTypeOptions: normalized.jobTypeOptions.map((item) => item.value),
        paymentOptions: normalized.paymentOptions.map((item) => item.value),
        shiftOptions: normalized.shiftOptions.map((item) => item.value),
      })
    );

    return normalized;
  } catch (error) {
    console.error("Failed to save app settings", error);
    return settings;
  }
}

export function getSettingSection(key) {
  return SETTING_SECTIONS.find((section) => section.key === key) || null;
}

export function mutateSettingOptions(currentSettings, settingKey, value, previousValue = null) {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    throw new Error("Value is required");
  }

  const settings = currentSettings || loadAppSettings();
  const options = [...(settings[settingKey] || [])];

  if (previousValue !== null) {
    const index = options.findIndex((item) => item.value === previousValue);
    if (index === -1) {
      throw new Error("Option not found");
    }
    if (options.some((item, idx) => idx !== index && item.value === trimmed)) {
      throw new Error("This value already exists");
    }
    options[index] = { value: trimmed, label: trimmed };
  } else {
    if (options.some((item) => item.value === trimmed)) {
      throw new Error("This value already exists");
    }
    options.push({ value: trimmed, label: trimmed });
  }

  return { ...settings, [settingKey]: options };
}

export function upsertSettingOption(settingKey, value, previousValue = null) {
  return saveAppSettings(mutateSettingOptions(loadAppSettings(), settingKey, value, previousValue));
}

export function removeSettingOption(currentSettings, settingKey, value) {
  const settings = currentSettings || loadAppSettings();
  const options = (settings[settingKey] || []).filter((item) => item.value !== value);
  return { ...settings, [settingKey]: options };
}

export function deleteSettingOption(settingKey, value) {
  return saveAppSettings(removeSettingOption(loadAppSettings(), settingKey, value));
}
